import { useCallback, useEffect, useRef } from 'react';

import type { GalleryVideoItem } from '@rover/react-lib/src/components/ImageGallery/ImageGallery.common';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { useCurrentUser } from '@rover/rsdk/src/modules/Data/Core/CurrentUser';
import { isAndroid, isIOS } from '@rover/rsdk/src/modules/Env/react';
import { getSourceDescription } from '@rover/rsdk/src/modules/Network/userAgent';

import { IntroVideoViewEntryPoint, IntroVideoViewEvent } from './analyticsEvents';

export const getPlatform = (): ReturnType<typeof getSourceDescription> => {
  if (isIOS()) return 'ios';
  if (isAndroid()) return 'android';
  return getSourceDescription();
};

type Params = {
  entryPoint: IntroVideoViewEntryPoint;
  sitterOpk: string;
  isLightboxOpen: boolean;
  cbsa?: string | null;
};

/**
 * Returns the `onVideoPlay` handler for an `ImageGallery` that shows a sitter's
 * intro video. The player reports every play and every resume, so the view is
 * counted once per lightbox open.
 */
const useIntroVideoViewTracking = ({
  entryPoint,
  sitterOpk,
  isLightboxOpen,
  cbsa = null,
}: Params): ((item: GalleryVideoItem) => void) => {
  const hasReportedRef = useRef(false);
  const viewerOpk = useCurrentUser({ refetchOnMount: false }).user?.opk ?? null;

  useEffect(() => {
    if (isLightboxOpen) hasReportedRef.current = false;
  }, [isLightboxOpen]);

  return useCallback(
    (item: GalleryVideoItem): void => {
      if (hasReportedRef.current) return;
      hasReportedRef.current = true;

      emitAnalyticsEvent(
        new IntroVideoViewEvent({
          entry_point: entryPoint,
          video_url: item.video.sources[0] ?? '',
          sitter_opk: sitterOpk,
          viewer_opk: viewerOpk,
          cbsa,
          platform: getPlatform(),
        })
      );
    },
    [entryPoint, sitterOpk, viewerOpk, cbsa]
  );
};

export default useIntroVideoViewTracking;
